import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { ACCENTS, type AccentName } from "../../lib/accents";

export interface IconChipProps {
  icon: LucideIcon;
  accent: AccentName;
  /** `sm` = 40px chip / 20px icon, `md` = 48px / 24px, `lg` = 56px / 28px. Default `md`. */
  size?: "sm" | "md" | "lg";
  className?: string;
}

// Literal size classes — Tailwind can't see interpolated strings.
const SIZES = {
  sm: { box: "h-10 w-10", icon: "h-5 w-5" },
  md: { box: "h-12 w-12", icon: "h-6 w-6" },
  lg: { box: "h-14 w-14", icon: "h-7 w-7" },
} as const;

/**
 * A soft, round icon badge in an accent hue (the landing's pebble shape, not
 * a square). The icon is decorative; the -600 icon on the -100 tint clears
 * 3:1 non-text contrast (lib/accents.ts). The white ring lifts it off a wash.
 */
export function IconChip({ icon: Icon, accent, size = "md", className }: IconChipProps) {
  const s = SIZES[size];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full ring-4 ring-white/70",
        s.box,
        ACCENTS[accent].chip,
        className,
      )}
    >
      <Icon className={s.icon} aria-hidden="true" />
    </span>
  );
}
